import { QuotationResponse, submitSangjoQuotationRequest } from "@/apis/sangjoQuotation";
import { QuotationRequestBody } from "@/types/quotationType";
import { AxiosError, AxiosResponse } from "axios";
import { cooKeys, pixelCustomEvent, PixelEventName, setCookie } from "goi_common";
import { useMutation } from "react-query";
import { naverConversionEvent } from "@/utils/naverPageView";
import { ensureFreshAuth } from "@/utils/axios";
import { useSetRecoilState } from "recoil";
import { isLoggedInState } from "@/atoms/accountState";
import { markQuotationResubmit } from "@/utils/quotationResubmit";

interface MutateSangjoQuotationParams {
  body: QuotationRequestBody;
  onSuccess?: (res: AxiosResponse<QuotationResponse>) => void;
  onError?: (error: AxiosError) => void;
}

const useSangjoQuotationRequests = () => {
  const setIsLoggedIn = useSetRecoilState(isLoggedInState);

  /**
   * 제출 직전에 인증 상태를 최신화한다. mutationFn 안에 두는 이유는 `isLoading`이
   * refresh 왕복까지 덮어야 caller의 이중 클릭 가드(`if (isLoading) return`)가 뚫리지 않아서다.
   *
   * refresh로 세션이 살아나면 recoil도 맞춰준다. atom은 마운트 시 쿠키를 1회만 읽어
   * 서버가 플래그 쿠키를 되살려도 앱은 계속 비로그인으로 알기 때문이다.
   * (2단계에서 뺀 `isLoggedIn` 의존과 방향이 반대다. 그때는 프론트가 인증 상태를 추측해
   *  method를 정하는 "분기 근거"였고, 여기는 서버가 알려준 사실을 반영하는 것이다.)
   *
   * 다만 이번 제출의 caller `onSuccess`는 렌더 시점 값을 캡처한 클로저라 여전히 옛 값을 본다.
   * 완전한 해소는 제출 응답(200=재제출 / 201=신규)으로 분기하는 PR3 작업이다.
   */
  const { mutate, isLoading } = useMutation<
    AxiosResponse<QuotationResponse>,
    AxiosError,
    QuotationRequestBody
  >(async (body) => {
    const didRefresh = await ensureFreshAuth();
    if (didRefresh) setIsLoggedIn(true);

    return submitSangjoQuotationRequest(body);
  });

  /** 제출 응답을 그대로 쿠키에 담는다. `id`는 이번에 저장된 Request id */
  const setQuotationRequestCookie = (data: QuotationResponse) => {
    setCookie(cooKeys.quotation_request, JSON.stringify(data));
  };

  const mutateSangjoQuotation = ({ body, onSuccess, onError }: MutateSangjoQuotationParams) => {
    /** 견적 제출 광고 전환. 제출 성공 시점에만 발화 */
    const handleMutationSuccess = (res: AxiosResponse<QuotationResponse>) => {
      /*
       * 재제출 판정. 이 자리인 이유 둘.
       *
       * 1. 쿠키 쓰기(`setQuotationRequestCookie`)가 아니라 제출 성공에 걸어야 한다.
       *    일부 퍼널이 `isLoggedIn` 이면 쿠키 쓰기를 건너뛰고 바로 라우팅해서, 쿠키 쪽에
       *    걸어두면 회원 재제출이 통째로 누락된다.
       * 2. caller 의 `onSuccess` 보다 먼저여야 한다. 그 안에서 쿠키를 덮어쓰기 때문에,
       *    뒤로 가면 "이번 제출 직전에 쿠키가 있었나"를 더 이상 볼 수 없다.
       */
      markQuotationResubmit(res.status);

      naverConversionEvent({ type: "add_to_cart" });
      pixelCustomEvent(PixelEventName.AddPaymentInfo);
      onSuccess?.(res);
    };

    /**
     * v2 전환으로 훅이 단일 발화하던 실패 안내가 전부 사라졌다.
     * - 409: v2가 회원에게 409 대신 200(merge)을 반환한다
     * - 401: 서버가 만료·무효 토큰을 401이 아니라 익명으로 처리한다.
     *   대신 제출 직전 `ensureFreshAuth`가 access를 되살려 애초에 익명으로 새지 않게 한다.
     *
     * 남은 실패는 네트워크·5xx·validation이라 진입점마다 처리가 다를 수 있어 caller에게 넘긴다.
     */
    mutate(body, {
      onSuccess: handleMutationSuccess,
      onError: (error) => onError?.(error),
    });
  };

  return {
    setQuotationRequestCookie,
    mutateSangjoQuotation,
    isLoading,
  };
};

export default useSangjoQuotationRequests;
